import { HeartBank } from "../../../managers/HeartBank.ts";
import template, { COUNTRY_FLAGS } from "./hb-phone.mjs";
import { render } from "lit-html";
import { HbHeader } from "../../hb-header/hb-header.ts";
import { HbMain } from "../../hb-main/hb-main.ts";
import { HbFooter } from "../hb-footer.ts";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

//TESTING: replace localhost => 127.0.0.1

export class HbPhone extends HTMLElement {
    private heartbank: HeartBank = new HeartBank();
    private modal: ModalInterface;
    private header = document.querySelector("hb-header") as HbHeader;
    private main = document.querySelector("hb-main") as HbMain;
    private footer = document.querySelector("hb-footer") as HbFooter;

    private phone: HTMLElement;
    private numberForm: HTMLFormElement;
    private codeForm: HTMLFormElement;
    private number: HTMLInputElement;
    private code: HTMLInputElement;
    private goBack: HTMLParagraphElement;
    private resend: HTMLParagraphElement;
    private removeNumber: HTMLParagraphElement;
    private message: HTMLParagraphElement;

    private country = "us";
    private countryCode = "+1";
    private nationalNumber = "";
    private cc: HTMLButtonElement;
    private span: HTMLSpanElement;
    private us: HTMLLIElement;
    private kh: HTMLLIElement;
    private fi: HTMLLIElement;
    private ua: HTMLLIElement;

    constructor() {
        super();
        render(template(null), this);

        this.phone = this.querySelector("#hb-phone")!;
        this.numberForm = this.querySelector("#hb-phone-number")!;
        this.codeForm = this.querySelector("#hb-phone-code")!;
        this.number = this.querySelector("#hb-phone-number-input")!;
        this.code = this.querySelector("#hb-phone-code-input")!;
        this.goBack = this.querySelector("#hb-phone-back")!;
        this.resend = this.querySelector("#hb-phone-resend")!;
        this.removeNumber = this.querySelector("#hb-phone-remove")!;
        this.message = this.querySelector("#hb-phone-message")!;

        this.cc = this.querySelector("#hb-phone-number-cc")!;
        this.span = this.cc.firstElementChild as HTMLSpanElement;
        this.us = this.querySelector("#hb-phone-number-us")!;
        this.kh = this.querySelector("#hb-phone-number-kh")!;
        this.fi = this.querySelector("#hb-phone-number-fi")!;
        this.ua = this.querySelector("#hb-phone-number-ua")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40",
            closable: true,
            onHide: () => {
                this.numberForm.reset();
                this.codeForm.reset();
                this.message.textContent = "";
                //window.location.hash = "#/humanity";
            },
            onShow: () => {
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "hb-phone",
            override: true
        };

        this.modal = new Modal(this.phone, modalOptions, instanceOptions);
    }

    connectedCallback() {
        this.us.addEventListener("click", () => {
            this.country = "us";
            this.countryCode = "+1";
            this.renderCountryCode();
            this.cc.click();
        });
        this.kh.addEventListener("click", () => {
            this.country = "kh";
            this.countryCode = "+855";
            this.renderCountryCode();
            this.cc.click();
        });
        this.fi.addEventListener("click", () => {
            this.country = "fi";
            this.countryCode = "+358";
            this.renderCountryCode();
            this.cc.click();
        });
        this.ua.addEventListener("click", () => {
            this.country = "ua";
            this.countryCode = "+380";
            this.renderCountryCode();
            this.cc.click();
        });

        this.numberForm.addEventListener(
            "submit",
            this.handleSendCode.bind(this)
        );
        this.codeForm.addEventListener(
            "submit",
            this.handleVerifyCode.bind(this)
        );

        this.resend.addEventListener("click", this.handleResendCode.bind(this));
        this.removeNumber.addEventListener(
            "click",
            this.handleRemoveNumber.bind(this)
        );
        this.goBack.addEventListener("click", () => {
            this.numberForm.classList.remove("hidden");
            this.codeForm.classList.add("hidden");
        });

        this.heartbank.addAuthStateListener((user) =>
            render(template(user), this)
        );
    }

    show() {
        this.modal.show();
    }

    hide() {
        this.modal.hide();
    }

    private renderCountryCode() {
        this.span.textContent =
            (COUNTRY_FLAGS as any)[this.countryCode] + " " + this.countryCode;
    }

    private async handleSendCode(event: Event) {
        event.preventDefault();
        this.nationalNumber = this.number.value.replace(/\D/g, "");
        const e164 = this.countryCode + this.nationalNumber;

        if (e164 === this.heartbank.user?.phoneNumber) {
            this.message.textContent = "Please enter a new phone number";
            this.message.style.color = "red";
            return;
        }

        // const phoneNumber = "+12025550123"; // Example
        // if (!phoneNumber.match(/^\+\d{10,15}$/)) {
        //     console.error("Invalid phone number format");
        //     return;
        // }

        this.main.showSpinner();
        const status = await this.heartbank.handleSendCode(
            e164,
            false,
            !!(this.heartbank.user?.phoneNumber)
        );

        if (status.color === "green") {
            this.numberForm.classList.add("hidden");
            this.codeForm.classList.remove("hidden");
            this.code.focus();
        }

        this.message.textContent = status.text;
        this.message.style.color = status.color;
        this.main.hideSpinner();
    }

    private async handleResendCode(event: Event) {
        //event.preventDefault();
        this.main.showSpinner();

        const status = await this.heartbank.handleSendCode(
            this.countryCode + this.nationalNumber, // e164
            true,
            !!(this.heartbank.user?.phoneNumber)
        );

        this.message.textContent = status.text;
        this.message.style.color = status.color;
        this.main.hideSpinner();
    }

    private async handleVerifyCode(event: Event) {
        event.preventDefault();
        this.main.showSpinner();

        const status = await this.heartbank.handleVerifyCode(
            this.country,
            this.countryCode,
            this.nationalNumber,
            this.code.value.trim(),
            !!(this.heartbank.user?.phoneNumber)
        );

        if (status.color === "green") {
            //render(template(status.user), this); // weird lit-html bug
            //this.renderCountryCode();

            this.number.placeholder = this.nationalNumber;
            this.number.value = "";
            this.numberForm.classList.remove("hidden");
            this.codeForm.classList.add("hidden");

            this.header.renderLockbox(this.heartbank.user); // passkeys
            this.header.renderSafe(this.heartbank.user!);
            this.main.renderShip(this.heartbank.user);
            this.footer.renderHumanity(this.heartbank.user);
            window.location.hash = "#/humanity/mithril";
        }

        this.message.textContent = status.text;
        this.message.style.color = status.color;
        this.main.hideSpinner();
    }

    // not used
    private async handleRemoveNumber(event: Event) {
        //event.preventDefault();
        this.main.showSpinner();
        const status = await this.heartbank.removePhoneNumber();

        if (status.color === "green") render(template(status.user), this);
        this.message.textContent = status.text;
        this.message.style.color = status.color;
        this.main.hideSpinner();
    }
}
